import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Heart, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MenuSection, MobileActiveSubsection, ServiceMenuLink } from "./types";

interface HeaderMobileNavProps {
  isMenuOpen: boolean;
  isClosing: boolean;
  headerHeight: number;
  menuSections: MenuSection[];
  isMenuLoading: boolean;
  serviceMenuLinks: ServiceMenuLink[];
  isLoggedIn: boolean;
  totalWishlistCount: number;
  onClose: () => void;
  tm: (text: string) => string;
  th: (key: string, fallback: string) => string;
}

export const HeaderMobileNav = ({
  isMenuOpen,
  isClosing,
  headerHeight,
  menuSections,
  isMenuLoading,
  serviceMenuLinks,
  isLoggedIn,
  totalWishlistCount,
  onClose,
  tm,
  th,
}: HeaderMobileNavProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileActiveSection, setMobileActiveSection] = useState<string | null>(null);
  const [mobileActiveSubsection, setMobileActiveSubsection] = useState<MobileActiveSubsection | null>(null);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);

  const handleMobileBack = () => {
    if (mobileActiveSubsection) {
      const section = menuSections.find((s) => s.title === mobileActiveSubsection.sectionTitle);
      if (section && section.subsections.length <= 1) {
        setMobileActiveSubsection(null);
        setMobileActiveSection(null);
      } else {
        setMobileActiveSubsection(null);
      }
    } else if (mobileActiveSection) {
      setMobileActiveSection(null);
    }
  };

  const handleClose = () => {
    setMobileActiveSection(null);
    setMobileActiveSubsection(null);
    setMobileServiceOpen(false);
    onClose();
  };

  if (!isMenuOpen) return null;

  return (
    <>
      <div
        className={`lg:hidden fixed inset-0 bg-background/80 backdrop-blur-md z-40 ${
          isClosing ? "animate-fade-out" : "animate-fade-in"
        }`}
        style={{ top: `${headerHeight}px` }}
        onClick={onClose}
      />

      <nav
        className={`lg:hidden border-t fixed left-0 right-0 bottom-0 z-50 bg-background ${
          isClosing ? "animate-fade-out" : "animate-slide-down"
        }`}
        style={{ top: `${headerHeight}px` }}
      >
        <div className="relative h-full overflow-hidden">
          {/* Main Menu */}
          <div
            className={`flex flex-col text-center px-4 py-4 transition-all duration-300 overflow-y-auto ${
              mobileActiveSection || mobileServiceOpen
                ? "opacity-0 -translate-x-full absolute inset-0"
                : "opacity-100 translate-x-0 h-full"
            }`}
          >
            <Link
              to="/"
              onClick={onClose}
              className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${
                location.pathname === "/" ? "font-semibold text-primary" : ""
              }`}
              style={{ fontWeight: 400 }}
              translate="no"
            >
              <span className="w-5 flex-shrink-0 flex justify-center">
                {location.pathname === "/" ? <span className="h-4 w-[2px] bg-primary" /> : null}
              </span>
              <span className="flex-1 text-center">{tm("ראשי")}</span>
              <span className="w-5 flex-shrink-0" />
            </Link>
            {isMenuLoading && menuSections.length === 0 && (
              <>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="border-b border-border/30 py-3">
                    <div className="h-4 w-24 bg-primary/10 rounded animate-pulse mx-auto" />
                  </div>
                ))}
              </>
            )}
            {menuSections.map((section) => {
              const currentPath = decodeURIComponent(location.pathname);
              const isSectionActive = section.directCollectionHandle
                ? currentPath === `/collections/${section.directCollectionHandle}`
                : section.titleCollectionHandles?.length &&
                  section.titleCollectionHandles.some((h) => currentPath === `/collections/${h}`)
                ? true
                : section.subsections.some((sub) =>
                    sub.collections.some((col) => currentPath === `/collections/${col.node.handle}`)
                  );

              const handleSectionTap = () => {
                if (section.subsections.length === 1 && section.subsections[0].collections.length === 1) {
                  navigate(`/collections/${section.subsections[0].collections[0].node.handle}`);
                  onClose();
                } else if (section.subsections.length === 1) {
                  setMobileActiveSection(section.title);
                  setMobileActiveSubsection({ sectionTitle: section.title, subsectionIndex: 0 });
                } else {
                  setMobileActiveSection(section.title);
                }
              };

              return (
                <div key={section.title} className="border-b border-border/30">
                  {section.directCollectionHandle ? (
                    <Link
                      to={`/collections/${section.directCollectionHandle}`}
                      onClick={onClose}
                      className={`text-sm text-primary/80 hover:text-primary flex items-center py-3 ${
                        isSectionActive ? "font-semibold text-primary" : ""
                      }`}
                      style={{ fontWeight: 400 }}
                    >
                      <span className="w-5 flex-shrink-0 flex justify-center">
                        {isSectionActive ? <span className="h-4 w-[2px] bg-primary" /> : null}
                      </span>
                      <span className="flex-1 text-center">{tm(section.title)}</span>
                      <span className="w-5 flex-shrink-0" />
                    </Link>
                  ) : (
                    <button
                      onClick={handleSectionTap}
                      className={`text-sm text-primary/80 hover:text-primary flex items-center w-full py-3 ${
                        isSectionActive ? "font-semibold text-primary" : ""
                      }`}
                      style={{ fontWeight: 400 }}
                    >
                      <span className="w-5 flex-shrink-0 flex justify-center">
                        {isSectionActive ? <span className="h-4 w-[2px] bg-primary" /> : null}
                      </span>
                      <span className="flex-1 text-center">{tm(section.title)}</span>
                      <span className="w-5 flex-shrink-0 flex justify-center">
                        {section.subsections.length > 0 && <ChevronLeft className="h-4 w-4" />}
                      </span>
                    </button>
                  )}
                </div>
              );
            })}
            <Link
              to="/blog"
              onClick={onClose}
              className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${
                location.pathname.startsWith("/blog") ? "font-semibold text-primary" : ""
              }`}
              style={{ fontWeight: 400 }}
              translate="no"
            >
              <span className="w-5 flex-shrink-0 flex justify-center">
                {location.pathname.startsWith("/blog") ? <span className="h-4 w-[2px] bg-primary" /> : null}
              </span>
              <span className="flex-1 text-center">{tm("בלוג")}</span>
              <span className="w-5 flex-shrink-0" />
            </Link>
            <Link
              to="/about"
              className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${
                location.pathname === "/about" ? "font-semibold text-primary" : ""
              }`}
              style={{ fontWeight: 400 }}
              translate="no"
            >
              <span className="w-5 flex-shrink-0 flex justify-center">
                {location.pathname === "/about" ? <span className="h-4 w-[2px] bg-primary" /> : null}
              </span>
              <span className="flex-1 text-center">{tm("אודות")}</span>
              <span className="w-5 flex-shrink-0" />
            </Link>
            <Link
              to="/gift-card"
              onClick={onClose}
              className={`notranslate text-sm text-primary/80 hover:text-primary transition-colors py-3 border-b border-border/30 flex items-center ${
                location.pathname === "/gift-card" ? "font-semibold text-primary" : ""
              }`}
              style={{ fontWeight: 400 }}
              translate="no"
            >
              <span className="w-5 flex-shrink-0 flex justify-center">
                {location.pathname === "/gift-card" ? <span className="h-4 w-[2px] bg-primary" /> : null}
              </span>
              <span className="flex-1 text-center">GiftCard</span>
              <span className="w-5 flex-shrink-0" />
            </Link>
            <button
              onClick={() => setMobileServiceOpen(true)}
              className={`notranslate text-sm text-primary/80 hover:text-primary flex items-center w-full py-3 border-b border-border/30 ${
                ["/contact", "/faq", "/ring-size-guide", "/accessibility"].includes(location.pathname)
                  ? "font-semibold text-primary"
                  : ""
              }`}
              style={{ fontWeight: 400 }}
              translate="no"
            >
              <span className="w-5 flex-shrink-0 flex justify-center">
                {["/contact", "/faq", "/ring-size-guide", "/accessibility"].includes(location.pathname) ? (
                  <span className="h-4 w-[2px] bg-primary" />
                ) : null}
              </span>
              <span className="flex-1 text-center">{th("customerService", "שירות לקוחות")}</span>
              <span className="w-5 flex-shrink-0 flex justify-center">
                <ChevronLeft className="h-4 w-4" />
              </span>
            </button>

            <div className="flex items-center justify-center gap-4 pt-4 border-t">
              <Button variant="ghost" size="icon" className="hover:bg-transparent relative" asChild>
                <Link to="/wishlist" onClick={onClose}>
                  <Heart className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  {totalWishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {totalWishlistCount}
                    </span>
                  )}
                </Link>
              </Button>
              <Button variant="ghost" size="icon" className="hover:bg-transparent relative" asChild>
                <Link
                  to={isLoggedIn ? "/profile" : "/auth"}
                  onClick={onClose}
                  aria-label={isLoggedIn ? th("personalArea", "האזור האישי") : th("login", "התחברות")}
                >
                  <User className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </Link>
              </Button>
            </div>
          </div>

          {/* Mobile Service Submenu */}
          <div
            className={`flex flex-col px-4 py-4 transition-all duration-300 absolute inset-0 bg-background overflow-y-auto notranslate ${
              mobileServiceOpen
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-full pointer-events-none"
            }`}
          >
            <button
              onClick={() => setMobileServiceOpen(false)}
              className="flex items-center gap-2 text-sm text-primary py-3 border-b border-border/30"
              style={{ fontWeight: 400 }}
            >
              <ChevronLeft className="h-4 w-4 rotate-180" />
              <span>{tm("חזרה")}</span>
            </button>
            <div className="text-center py-3 border-b border-border/30 text-primary font-semibold text-base">
              {th("customerService", "שירות לקוחות")}
            </div>
            {serviceMenuLinks.map((link) => (
              <Link
                key={link.href}
                to={link.requiresAuth && !isLoggedIn ? "/auth" : link.href}
                onClick={() => {
                  if (link.requiresAuth && !isLoggedIn) {
                    navigate("/auth", { state: { redirectTo: link.href } });
                  }
                  setMobileServiceOpen(false);
                  onClose();
                }}
                className={`py-3 border-b border-border/30 text-sm text-primary/80 hover:text-primary flex items-center ${
                  location.pathname === link.href.split("?")[0] ? "font-semibold text-primary" : ""
                }`}
                style={{ fontWeight: 400 }}
              >
                <span className="w-5 flex-shrink-0" />
                <span className="flex-1 text-center">{link.title}</span>
                <span className="w-5 flex-shrink-0" />
              </Link>
            ))}
          </div>

          {/* Section Submenu */}
          {menuSections.map((section) => (
            <div
              key={`submenu-${section.title}`}
              className={`flex flex-col px-4 py-4 transition-all duration-300 absolute inset-0 bg-background overflow-y-auto ${
                mobileActiveSection === section.title && !mobileActiveSubsection
                  ? "opacity-100 translate-x-0"
                  : mobileActiveSection === section.title && mobileActiveSubsection
                  ? "opacity-0 -translate-x-full"
                  : "opacity-0 translate-x-full pointer-events-none"
              }`}
            >
              <button
                onClick={handleMobileBack}
                className="flex items-center gap-2 text-sm text-primary py-3 border-b border-border/30"
                style={{ fontWeight: 400 }}
              >
                <ChevronLeft className="h-4 w-4 rotate-180" />
                <span>{tm("חזרה")}</span>
              </button>
              <div className="text-center py-3 border-b border-border/30 text-primary font-semibold text-base">
                {tm(section.title)}
              </div>
              {section.subsections.map((subsection, idx) => (
                <button
                  key={idx}
                  onClick={() =>
                    setMobileActiveSubsection({ sectionTitle: section.title, subsectionIndex: idx })
                  }
                  className="flex items-center py-3 border-b border-border/30 text-sm text-primary/80 hover:text-primary"
                  style={{ fontWeight: 400 }}
                >
                  <span className="w-5 flex-shrink-0" />
                  <span className="flex-1 text-center">{tm(subsection.title || section.title)}</span>
                  <span className="w-5 flex-shrink-0 flex justify-center">
                    <ChevronLeft className="h-4 w-4" />
                  </span>
                </button>
              ))}
            </div>
          ))}

          {/* Subsection Collections */}
          {menuSections.map((section) =>
            section.subsections.map((subsection, idx) => (
              <div
                key={`collections-${section.title}-${idx}`}
                className={`flex flex-col px-4 py-4 transition-all duration-300 absolute inset-0 bg-background overflow-y-auto ${
                  mobileActiveSubsection?.sectionTitle === section.title &&
                  mobileActiveSubsection?.subsectionIndex === idx
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-full pointer-events-none"
                }`}
              >
                <button
                  onClick={handleMobileBack}
                  className="flex items-center gap-2 text-sm text-primary py-3 border-b border-border/30"
                  style={{ fontWeight: 400 }}
                >
                  <ChevronLeft className="h-4 w-4 rotate-180" />
                  <span>{tm("חזרה")}</span>
                </button>
                <div className="text-center py-3 border-b border-border/30 text-primary font-semibold text-base">
                  {tm(subsection.title || section.title)}
                </div>
                {subsection.collections.map((collection) => (
                  <Link
                    key={collection.node.id}
                    to={`/collections/${collection.node.handle}`}
                    onClick={handleClose}
                    className={`py-3 border-b border-border/30 text-sm text-primary/80 hover:text-primary flex items-center ${
                      decodeURIComponent(location.pathname) === `/collections/${collection.node.handle}`
                        ? "font-semibold text-primary"
                        : ""
                    }`}
                    style={{ fontWeight: 400 }}
                  >
                    <span className="w-5 flex-shrink-0" />
                    <span className="flex-1 text-center">
                      {subsection.collectionTitles?.[collection.node.handle] || tm(collection.node.title)}
                    </span>
                    <span className="w-5 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            ))
          )}
        </div>
      </nav>
    </>
  );
};
